import { create } from 'zustand'
import { parseUpdateStatus } from '../utils/appUpdate'

/** An update announced and not yet started; times are epoch milliseconds. */
export interface UpcomingUpdate {
  startsAt: number
  downtimeSeconds: number
}

interface AppUpdateState {
  /**
   * Whether the API has said the app is updating (a deploy holding every
   * request; see `isUpdatingResponse`). It stays set until the page reloads
   * onto the new build: AppUpdateController shows the updating screen and
   * reloads once the API answers again. Not reset at sign-in: an update
   * holds the whole app, whoever is signed in.
   */
  updating: boolean
  /** When the update should be over, from the updating document; null when it didn't say. */
  estimatedEndAt: number | null
  /** An update the proxy has announced (GET /api/update-status), until it starts or is called off. */
  upcoming: UpcomingUpdate | null
  /** Whether the updating modal was closed to show the page frozen beneath it. */
  frozenPageShown: boolean
  markUpdating: (estimatedEndAt?: number | null) => void
  setUpcoming: (upcoming: UpcomingUpdate | null) => void
  showFrozenPage: () => void
}

export const useAppUpdateStore = create<AppUpdateState>((set) => ({
  updating: false,
  estimatedEndAt: null,
  upcoming: null,
  frozenPageShown: false,
  markUpdating: (estimatedEndAt = null) =>
    set((state) => ({
      updating: true,
      upcoming: null,
      // The first estimate stands unless a later reply gives one where it had none.
      estimatedEndAt: state.estimatedEndAt ?? estimatedEndAt,
    })),
  setUpcoming: (upcoming) =>
    set((state) =>
      state.upcoming?.startsAt === upcoming?.startsAt &&
      state.upcoming?.downtimeSeconds === upcoming?.downtimeSeconds
        ? state
        : { upcoming }
    ),
  showFrozenPage: () => set({ frozenPageShown: true }),
}))

/**
 * Records that a reply said the app is updating (the API client and the chat
 * stream call it), with its body when there is one: an updating 503 says
 * when the update should be over.
 */
export function markAppUpdating(body?: unknown): void {
  const status = body === undefined ? null : parseUpdateStatus(body)
  const estimatedEndAt = status?.state === 'updating' ? status.estimatedEndAt : null
  const state = useAppUpdateStore.getState()
  if (!state.updating || (state.estimatedEndAt === null && estimatedEndAt !== null)) {
    state.markUpdating(estimatedEndAt)
  }
}
